import {
  $applyNodeReplacement,
  TextNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
} from 'lexical';

export type SerializedMentionNode = Spread<{ mentionName: string }, SerializedTextNode>;

// A tagged "@Name" — behaves like ordinary text for serialization/copy-paste (its
// getTextContent() is just "@Name", so the note still saves as a plain string with the
// mention embedded, no different from typing it by hand) but renders as one atomic,
// specially-styled unit: arrow keys skip over it and backspace at its edge deletes the
// whole thing in one go, matching the "tag" mental model rather than "text you can type
// into". Modeled directly on Lexical's own reference mentions implementation.
export class MentionNode extends TextNode {
  __mention: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mention, node.__text, node.__key);
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    // $createMentionNode and MentionNode reference each other; both are fully defined by
    // the time either is actually called, so this is safe despite the declaration order.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const node = $createMentionNode(serializedNode.mentionName);
    node.setTextContent(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setStyle(serializedNode.style);
    return node;
  }

  constructor(mentionName: string, text?: string, key?: NodeKey) {
    super(text ?? mentionName, key);
    this.__mention = mentionName;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionName: this.__mention,
      type: 'mention',
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = 'rounded bg-blue-100 text-blue-600';
    return dom;
  }

  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }
}

// `mentionName` is the full display text, e.g. "@Jane Doe" — callers are responsible for
// prefixing the "@" themselves.
export function $createMentionNode(mentionName: string): MentionNode {
  const node = new MentionNode(mentionName);
  node.setMode('segmented').toggleDirectionless();
  return $applyNodeReplacement(node);
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
